// src/encounter/services/use-vitals-history.ts
// Longitudinal, patient-level vitals history for the pediatric GrowthChart (PROD-1324).
// GET /api/Patients/GetVitalsHistoryByCode?PatientId=&Code=<LOINC> → PatientVital[] (bare array),
// one call per growth metric (Flutter patient_chart_data_source_impl.dart:1040 getVitalsHistoryByCode).
// This is the LONGITUDINAL series (all readings for one vital over the patient's life), distinct
// from useEncounterVitals (the CURRENT encounter's single reading). Runs the metric fetches in
// parallel via useQueries; returns a LOINC→PatientVital[] map for to-growth-series.
import { useQueries, useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PatientVital } from '@/types/vitals-api';
import { VITAL_CODES } from '@/types/vitals-api';
import { GROWTH_METRIC_CODES } from '../lib/to-growth-series';

const GROWTH_CODES = Array.from(new Set(Object.values(GROWTH_METRIC_CODES)));

export interface VitalsHistoryResult {
  /** LOINC → that vital's full history (empty object until enabled/loaded). */
  byCode: Partial<Record<string, PatientVital[]>>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetch the growth-metric vitals histories (length/weight/head/bmi) for a patient. Gated on
 * `patientId` + `enabled` (the caller only turns it on for a pediatric patient, so we don't fan out
 * four calls for every encounter). Each metric is its own query so one slow/failed code doesn't block
 * the others.
 */
export function useVitalsHistory(
  patientId: string | null | undefined,
  enabled: boolean,
): VitalsHistoryResult {
  const on = !!patientId && enabled;
  const queries = useQueries({
    queries: GROWTH_CODES.map((code) => ({
      queryKey: ['vitals-history', patientId, code] as const,
      enabled: on,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        get<PatientVital[]>(
          `/api/Patients/GetVitalsHistoryByCode?PatientId=${encodeURIComponent(
            patientId as string,
          )}&Code=${encodeURIComponent(code)}`,
          { signal },
        ),
    })),
  });

  const byCode: Partial<Record<string, PatientVital[]>> = {};
  GROWTH_CODES.forEach((code, i) => {
    const data = queries[i]?.data;
    if (data) byCode[code] = data;
  });

  return {
    byCode,
    isLoading: on && queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
}

/** Max BMI history points fed to the vitals-band sparkline (newest N, oldest→newest). */
const BMI_SPARKLINE_POINTS = 12;

/**
 * Single-code longitudinal BMI history → the numeric series for the BMI tile's inline
 * sparkline (Figma 613:2118 "Edit Vitals": BMI carries a trend line; parity loop 2).
 * ONE call — deliberately not useVitalsHistory (which fans out all four growth codes and
 * is gated to pediatric patients for exactly that reason). Shares the
 * ['vitals-history', patientId, code] queryKey with the growth queries, so a pediatric
 * encounter that already fetched BMI reuses the cache instead of re-fetching.
 */
export function useBmiHistory(patientId: string | null | undefined, enabled: boolean): number[] {
  const on = !!patientId && enabled;
  const { data } = useQuery({
    queryKey: ['vitals-history', patientId, VITAL_CODES.BMI] as const,
    enabled: on,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      get<PatientVital[]>(
        `/api/Patients/GetVitalsHistoryByCode?PatientId=${encodeURIComponent(
          patientId as string,
        )}&Code=${encodeURIComponent(VITAL_CODES.BMI)}`,
        { signal },
      ),
  });
  if (!data?.length) return [];
  return data
    .filter((v) => !v.isDeleted)
    .map((v) => ({
      t: v.effectiveDateTime ?? v.createdDate ?? '',
      n: Number(v.quantity?.value),
    }))
    .filter((p) => Number.isFinite(p.n))
    .sort((a, b) => a.t.localeCompare(b.t))
    .slice(-BMI_SPARKLINE_POINTS)
    .map((p) => p.n);
}
